import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Modal,
  Form,
  Select,
  Input,
  DatePicker,
  Card,
  Alert,
  Spin,
  Empty,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  EyeOutlined,
  ReloadOutlined,
  BarChartOutlined,
  DownloadOutlined,
  AuditOutlined,
  LockOutlined,
  TeamOutlined,
  TagsOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  FileOutlined,
} from '@ant-design/icons';
import {
  fetchAuditLogs,
  fetchAuditLog,
  fetchAuditSummary,
  setAuditFilters,
  clearAuditFilters,
  setAuditPagination,
} from '../../redux/actions/auditLogActions';
import { hasPermission } from '../../utils/permissions';
import './AuditLogs.css';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditLogs = () => {
  const dispatch = useDispatch();
  const {
    logs,
    currentLog,
    summary,
    entityTypes,
    auditActions,
    loading,
    summaryLoading,
    error,
    filters,
    pagination,
  } = useSelector((state) => state.auditLogs);

  // Get permissions from Redux store
  const permissions = useSelector(state => state.auth.permissions || []);
  
  // Check permissions
  const canViewGrid = hasPermission(permissions, 'audit/gridView');
  const canExport = hasPermission(permissions, 'audit/export');

  const [form] = Form.useForm();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // Initialize component
  useEffect(() => {
    if (canViewGrid) {
      dispatch(fetchAuditLogs());
      dispatch(fetchAuditSummary());
    }
  }, [dispatch, canViewGrid]);

  // Handle filter changes
  const handleFilterChange = useCallback((changedFields) => {
    const newFilters = { ...filters, ...changedFields };
    dispatch(setAuditFilters(newFilters));
  }, [dispatch, filters]);

  // Handle search
  const handleSearch = useCallback(() => {
    dispatch(setAuditPagination({ offset: 0 }));
    dispatch(fetchAuditLogs());
  }, [dispatch]);

  // Handle filter clear
  const handleClearFilters = useCallback(() => {
    form.resetFields();
    dispatch(clearAuditFilters());
    dispatch(setAuditPagination({ offset: 0 }));
    dispatch(fetchAuditLogs());
  }, [dispatch, form]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    dispatch(fetchAuditLogs());
    dispatch(fetchAuditSummary());
  }, [dispatch]);

  // Handle view details
  const handleViewDetails = useCallback((record) => {
    dispatch(fetchAuditLog(record.id));
    setDetailModalVisible(true);
  }, [dispatch]);

  // Handle pagination change
  const handleTableChange = useCallback((page, pageSize) => {
    const offset = (page - 1) * pageSize;
    dispatch(setAuditPagination({ 
      offset, 
      limit: pageSize 
    }));
    dispatch(fetchAuditLogs());
  }, [dispatch]);

  // Get action color
  const getActionColor = (action) => {
    const colors = {
      CREATE: 'success',
      UPDATE: 'warning',
      DELETE: 'error',
    };
    return colors[action] || 'default';
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      vendor: <TeamOutlined />,
      kp_product: <TagsOutlined />,
      kp_delivery_address: <EnvironmentOutlined />,
      kp_udhyam_category: <AuditOutlined />,
      kp_product_category: <AppstoreOutlined />,
    };
    return icons[entityType] || <FileOutlined />;
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = moment(timestamp);
    return {
      dateTime: date.format('MMM DD, YYYY HH:mm'),
      timeAgo: date.fromNow(),
    };
  };

  // Format JSON data
  const formatJsonData = (data) => {
    if (!data) return 'N/A';
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return data;
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp) => {
        const formatted = formatTimestamp(timestamp);
        return (
          <div className="timestamp-info">
            <span className="date-time">{formatted.dateTime}</span>
            <span className="time-ago">{formatted.timeAgo}</span>
          </div>
        );
      },
    },
    {
      title: 'Entity',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 120,
      render: (entityType, record) => (
        <div>
          <Tag className="entity-tag">
            {getEntityIcon(entityType)} {entityType.replace('kp_', '')}
          </Tag>
          {record.entity_name && (
            <div className="entity-name">{record.entity_name}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action) => (
        <Tag 
          color={getActionColor(action)} 
          className="action-tag"
        >
          {action}
        </Tag>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user_email',
      key: 'user_email',
      width: 200,
      render: (email, record) => (
        <div className="user-info">
          <span>{email}</span>
          <span className="user-email">ID: {record.user_id}</span>
        </div>
      ),
    },
    {
      title: 'Changes',
      dataIndex: 'changed_fields',
      key: 'changed_fields',
      width: 200,
      render: (changedFields) => {
        if (!changedFields || !Array.isArray(changedFields)) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <div className="changed-fields">
            {changedFields.slice(0, 3).map((field) => (
              <span key={field} className="field-tag">{field}</span>
            ))}
            {changedFields.length > 3 && (
              <span className="field-tag">+{changedFields.length - 3} more</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Entity ID',
      dataIndex: 'entity_id',
      key: 'entity_id',
      width: 100,
      render: (id) => <Text code>{id}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          className="details-button"
        >
          Details
        </Button>
      ),
    },
  ];

  // Summary cards
  const renderSummaryCards = () => {
    if (!summary?.summary) return null;

    return (
      <div className="summary-cards">
        {summary.summary.map((item) => (
          <Card key={item.entity_type} className="summary-card">
            <div className="summary-card-header">
              <h3 className="summary-card-title">
                {getEntityIcon(item.entity_type)} {item.entity_type.replace('kp_', '').replace('_', ' ')}
              </h3>
              <span className="summary-card-icon">
                <BarChartOutlined />
              </span>
            </div>
            <div className="summary-stats">
              <div className="summary-stat">
                <span className="stat-value create">{item.create_count}</span>
                <div className="stat-label">Created</div>
              </div>
              <div className="summary-stat">
                <span className="stat-value update">{item.update_count}</span>
                <div className="stat-label">Updated</div>
              </div>
              <div className="summary-stat">
                <span className="stat-value delete">{item.delete_count}</span>
                <div className="stat-label">Deleted</div>
              </div>
            </div>
            {item.most_active_user && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
                Most active: {item.most_active_user}
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  // Audit detail modal
  const renderDetailModal = () => {
    if (!currentLog) return null;

    const formatted = formatTimestamp(currentLog.timestamp);

    return (
      <Modal
        title="Audit Log Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
        className="audit-detail-modal"
      >
        <div className="audit-detail-body">
          <div className="detail-section">
            <h4 className="detail-section-title">General Information</h4>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-label">Entity Type</div>
                <div className="detail-value">
                  <Tag className="entity-tag">
                    {getEntityIcon(currentLog.entity_type)} {currentLog.entity_type}
                  </Tag>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Action</div>
                <div className="detail-value">
                  <Tag color={getActionColor(currentLog.action)} className="action-tag">
                    {currentLog.action}
                  </Tag>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Entity ID</div>
                <div className="detail-value">
                  <Text code>{currentLog.entity_id}</Text>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">User</div>
                <div className="detail-value">
                  {currentLog.user_email} (ID: {currentLog.user_id})
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Timestamp</div>
                <div className="detail-value">
                  <div>{formatted.dateTime}</div>
                  <Text type="secondary">{formatted.timeAgo}</Text>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">IP Address</div>
                <div className="detail-value">
                  <Text code>{currentLog.ip_address || 'N/A'}</Text>
                </div>
              </div>
            </div>
          </div>

          {currentLog.changed_fields && currentLog.changed_fields.length > 0 && (
            <div className="detail-section">
              <h4 className="detail-section-title">Changed Fields</h4>
              <div className="changed-fields">
                {currentLog.changed_fields.map((field) => (
                  <span key={field} className="field-tag">{field}</span>
                ))}
              </div>
            </div>
          )}

          {(currentLog.old_data || currentLog.new_data) && (
            <div className="detail-section">
              <h4 className="detail-section-title">Data Changes</h4>
              <div className="data-comparison">
                {currentLog.old_data && (
                  <div className="data-section">
                    <div className="data-section-header">Previous Data</div>
                    <div className="data-section-content">
                      <div className="json-viewer">
                        {formatJsonData(currentLog.old_data)}
                      </div>
                    </div>
                  </div>
                )}
                {currentLog.new_data && (
                  <div className="data-section">
                    <div className="data-section-header">Current Data</div>
                    <div className="data-section-content">
                      <div className="json-viewer">
                        {formatJsonData(currentLog.new_data)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentLog.user_agent && (
            <div className="detail-section">
              <h4 className="detail-section-title">Technical Information</h4>
              <div className="detail-item">
                <div className="detail-label">User Agent</div>
                <div className="detail-value">
                  <Text style={{ fontSize: '12px' }}>{currentLog.user_agent}</Text>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  // If user doesn't have permission to view grid, show message
  if (!canViewGrid) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><LockOutlined /></div>
          <div className="empty-state-text">
            You don't have permission to view audit logs.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><AuditOutlined /></div>
            <div className="page-header-text">
              <h1>Audit Logs</h1>
              <p>Track and monitor all changes made to critical business entities</p>
            </div>
          </div>
          <div className="page-header-actions">
            <button
              className="btn btn-secondary"
              onClick={handleRefresh}
            >
              <ReloadOutlined /> Refresh
            </button>
            <button
              className={showSummary ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => setShowSummary(!showSummary)}
            >
              <BarChartOutlined /> {showSummary ? 'Hide' : 'Show'} Summary
            </button>
            {canExport && (
              <button
                className="btn btn-success"
                onClick={() => {
                  // TODO: Implement export functionality
                  console.log('Export audit logs');
                }}
              >
                <DownloadOutlined /> Export
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            ⚠️ {error}
          </div>
        )}

        {/* Summary Cards */}
        {showSummary && (
          <>
            {summaryLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading summary...</p>
              </div>
            ) : summary?.summary && summary.summary.length > 0 ? (
              <div className="stats-grid">
                {summary.summary.map((item) => (
                  <div key={item.entity_type} className="stat-card">
                    <div className="stat-icon">{getEntityIcon(item.entity_type)}</div>
                    <div className="stat-value">{item.create_count + item.update_count + item.delete_count}</div>
                    <div className="stat-label">{item.entity_type.replace('kp_', '').replace('_', ' ')}</div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.75rem' }}>
                      <span className="badge badge-success">{item.create_count} Created</span>
                      <span className="badge badge-warning">{item.update_count} Updated</span>
                      <span className="badge badge-danger">{item.delete_count} Deleted</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}

        {/* Filters */}
        <div className="card">
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            const dateRange = allValues.dateRange;
            const filterValues = { ...allValues };
            
            if (dateRange && dateRange.length === 2) {
              filterValues.start_date = dateRange[0].format('YYYY-MM-DD');
              filterValues.end_date = dateRange[1].format('YYYY-MM-DD');
            }
            delete filterValues.dateRange;
            
            handleFilterChange(filterValues);
          }}
          initialValues={filters}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Entity Type" name="entity_type">
                <Select
                  placeholder="Select entity type"
                  allowClear
                >
                  {entityTypes.map((type) => (
                    <Option key={type.type} value={type.type}>
                      {getEntityIcon(type.type)} {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Action" name="action">
                <Select
                  placeholder="Select action"
                  allowClear
                >
                  {auditActions.map((action) => (
                    <Option key={action.action} value={action.action}>
                      {action.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Entity ID" name="entity_id">
                <Input placeholder="Enter entity ID" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="User ID" name="user_id">
                <Input placeholder="Enter user ID" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Date Range" name="dateRange">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={16}>
              <Form.Item label=" " style={{ marginBottom: 0 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={loading}
                  >
                    Search
                  </Button>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                  >
                    Clear
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Button>
                  <Button
                    type={showSummary ? 'primary' : 'default'}
                    icon={<BarChartOutlined />}
                    onClick={() => setShowSummary(!showSummary)}
                  >
                    {showSummary ? 'Hide' : 'Show'} Summary
                  </Button>
                  {canExport && (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        // TODO: Implement export functionality
                        console.log('Export audit logs');
                      }}
                    >
                      Export
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        </div>

        {/* Audit Logs Table */}
        <div className="modern-table-container">
        <Table
          className="audit-table"
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          pagination={{
            current: Math.floor(pagination.offset / pagination.limit) + 1,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entries`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No audit logs found"
              />
            ),
          }}
          scroll={{ x: 1000 }}
        />
        </div>

        {/* Detail Modal */}
        {renderDetailModal()}
      </div>
    </div>
  );
};

export default AuditLogs;
